import React, { useCallback, useEffect, useState } from "react";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { PartnerOfferingLocation } from "../../data/partner_offering";
import { useI18n } from "../../lib/i18n/i18n_provider";
import DropdownMenu from "react-bootstrap/esm/DropdownMenu";
import { Icon } from "../../components";
import { useAnalytics } from "../../lib/analytics/analytics";
import { Location } from "../../data";
import { isPartnerLocation } from "../../data/api";

interface SpacePickerProps {
  locations: PartnerOfferingLocation[];
  value?: string;
  onChange: (spaceID: string) => void;
  disabled: boolean;
}

export function SpacePicker(props: SpacePickerProps) {
  const { value, locations, onChange, disabled = false } = props;

  const options = locations.map((l) => ({
    label: l.locationDisplay
      ? l.locationDisplay
      : `${l.location.city} - ${l.location.streetAddress}`,
    value: l.spaceID,
  }));

  const analytics = useAnalytics();

  const [selectedLocation, setSelectedLocation] = useState<string>();
  const onSelectLocation = useCallback(
    (value: any) => {
      const location = options.find((l) => l.value === value);
      setSelectedLocation(location?.label);
      onChange(value);

      const pl = locations.find((l) => l.spaceID === value);

      if (!pl) {
        return;
      }

      analytics.event(
        "Location Select Click",
        {
          "Location Name": pl.location.name,
          "Location UUID": pl.location.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, onChange, options, locations]
  );

  useEffect(() => {
    if (value) {
      const location = options.find((l) => l.value === value);
      setSelectedLocation(location?.label);
    } else {
      setSelectedLocation("");
    }
  }, [value, setSelectedLocation, options]);

  return (
    <LocationPicker
      onChange={onSelectLocation}
      value={selectedLocation}
      options={options}
      disabled={disabled}
    />
  );
}

interface MembershipLocationPickerWrapperProps {
  locations: Location[];
  value?: string;
  onChange: (spaceID: string) => void;
  disabled: boolean;
  offeringLocations?: PartnerOfferingLocation[];
}

export function MembershipLocationPickerWrapper(
  props: MembershipLocationPickerWrapperProps
) {
  const {
    value,
    locations,
    onChange,
    disabled = false,
    offeringLocations,
  } = props;

  const options = locations.map((l) => {
    const offeringLocation = offeringLocations
      ? offeringLocations.find(
          (ol) =>
            !!ol.membership?.locatable &&
            isPartnerLocation(ol.membership.locatable) &&
            ol.membership.locatable.id === l.id
        )
      : null;

    const isSoldOut = offeringLocation?.membership?.inventory === 0;

    return {
      label: l.name.trim(),
      value: l.id,
      soldOut: isSoldOut,
    };
  });

  const analytics = useAnalytics();

  const [selectedLocation, setSelectedLocation] = useState<string>();
  const onSelectLocation = useCallback(
    (value: any) => {
      const location = options.find((l) => l.value === value);
      setSelectedLocation(location?.label.trim());
      onChange(value);

      const pl = locations.find((l) => l.id === value);

      if (!pl) {
        return;
      }

      analytics.event(
        "Location Select Click",
        {
          "Location Name": pl.name,
          "Location UUID": pl.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [options, onChange, locations, analytics]
  );

  useEffect(() => {
    if (value) {
      const location = options.find((l) => l.value === value);
      setSelectedLocation(location?.label.trim());
    } else {
      setSelectedLocation("");
    }
  }, [value, setSelectedLocation, options, locations]);

  return (
    <MembershipLocationPicker
      onChange={onSelectLocation}
      value={selectedLocation}
      options={options}
      disabled={disabled}
    />
  );
}

interface OptionSelector {
  value: string;
  label: string;
  soldOut?: boolean;
}

interface LocationPickerProps {
  options: OptionSelector[];
  value?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

export function LocationPicker(props: LocationPickerProps) {
  const { onChange, options, value, disabled } = props;
  const { fbt } = useI18n();

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        as={ButtonGroup}
        className="checkout-box-picker row"
        id={`location-picker`}
        size="lg"
        onSelect={(value: any) => onChange(value)}
      >
        <Dropdown.Toggle disabled={disabled}>
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {fbt("Location")}
              </span>
              <span className="row checkout-box-picker-selected">
                {value ? (
                  value
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select a location")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <DropdownMenu
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                },
              },
            ],
          }}
        >
          <Dropdown.Header>
            <div className="dropdown-header">
              <span className="row fw-bold text-uppercase">
                {fbt("Select a location")}
              </span>
            </div>
          </Dropdown.Header>
          {options.map((opt, index) => (
            <>
              <Dropdown.Item
                as="button"
                eventKey={opt.value}
                active={opt.label === value}
              >
                {opt.label}
              </Dropdown.Item>
              {index !== options.length - 1 && <Dropdown.Divider />}
            </>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

interface MembershipLocationPickerProps {
  options: OptionSelector[];
  value?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

export function MembershipLocationPicker(props: MembershipLocationPickerProps) {
  const { onChange, options, value, disabled } = props;
  const { fbt } = useI18n();

  const selectedOption = options.find((opt) => opt.label === value);

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        as={ButtonGroup}
        className="checkout-box-picker row"
        id={`location-picker`}
        size="lg"
        onSelect={(value: any) => onChange(value)}
      >
        <Dropdown.Toggle disabled={disabled}>
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {fbt("Location")}
              </span>
              <span className="row checkout-box-picker-selected">
                {value ? (
                  <span style={{ textAlign: "left" }} className="p-0">
                    <span className="p-0">{value}</span>
                    {selectedOption?.soldOut ? (
                      <>
                        <span> -</span>
                        <span className="p-0 text-danger">
                          {" "}
                          {fbt("Sold out")}
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </span>
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select a location")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <DropdownMenu>
          <Dropdown.Header>
            <div className="dropdown-header">
              <span className="row fw-bold text-uppercase">
                {fbt("Select a location")}
              </span>
            </div>
          </Dropdown.Header>
          {options.map((opt, index) => (
            <>
              <Dropdown.Item
                as="button"
                eventKey={opt.value}
                active={opt.label === value}
                disabled={opt.soldOut}
              >
                <span className="p-0">
                  {opt.label}
                  {opt.soldOut ? (
                    <span style={{ textAlign: "left" }} className="p-0">
                      <span> -</span>
                      <span className="p-0 text-danger">
                        {" "}
                        {fbt("Sold out")}
                      </span>
                    </span>
                  ) : (
                    ""
                  )}
                </span>
              </Dropdown.Item>
              {index !== options.length - 1 && <Dropdown.Divider />}
            </>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
